import { SetRequired } from 'type-fest'

import {
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_BUNDLE_AND_SAVE,
  OFFER_TYPE_CRUISE,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_TOUR_V2,
  OFFER_TYPE_VILLA,
} from 'constants/offer'
import { groupBy, last, nonNullable, sum, without } from 'lib/array/arrayUtils'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import {
  BookmarkPayload,
  BookmarkRequest,
  FlightBookmarkPayload,
  HotelBookmarkPayload,
} from 'tripPlanner/api/bookmark/types'
import { createTripItemBatch } from 'tripPlanner/api/tripItem'
import { assertUnreachable } from 'tripPlanner/utils'
import {
  mapToFareClass,
  prependCarrierCodeIfNeeded,
} from 'tripPlanner/utils/bookmarks/common'
import { AllCheckoutItemViews } from 'checkout/selectors/view/getAllItemViewsByType'

export type SingleTripItemCreationPayload = Parameters<
  typeof createTripItemBatch
>[0]['tripItems'][number]

function hasOccupancies(
  itemGroup: Array<
    | App.Checkout.LEAccommodationItemView
    | App.Checkout.BedbankAccommodationItemView
  >,
): itemGroup is Array<
  SetRequired<App.Checkout.LEAccommodationItemView, 'occupancy'>
> {
  return itemGroup.every((itemView) => !!itemView.occupancy)
}

function hasPricesAndValues<
  V extends
    | App.Checkout.LEAccommodationItemView
    | App.Checkout.BedbankAccommodationItemView,
>(
  itemGroup: Array<V>,
): itemGroup is Array<V & { price: number; value: number }> {
  return itemGroup.every(
    (itemView) =>
      typeof itemView.totals.price === 'number' && typeof itemView.totals.value === 'number',
  )
}

function getAccomPrice<
  V extends
    | App.Checkout.LEAccommodationItemView
    | App.Checkout.BedbankAccommodationItemView,
>(itemView: V & { price: number }) {
  return itemView.totals.price + (itemView.totals.otherFees?.propertyFees ?? 0)
}

export function createBookmarkPayloadsFromCheckout(
  allItemViews: AllCheckoutItemViews,
  cartId: string,
): BookmarkRequest['items'] {
  if (!allItemViews.hasRequiredData) {
    throw new Error('Missing required data to create bookmark payloads')
  }

  const {
    accommodationItemsView: { data: accommodation },
    experienceItemsView: { data: experiences },
    flightItemsView: { data: flights },
    villaItemsView: { data: villas },
  } = allItemViews.data

  let payloads: BookmarkRequest['items'] = []

  for (const accommOfferView of accommodation) {
    const { offerId, startDate, endDate } = accommOfferView
    if (
      accommOfferView.offerType === OFFER_TYPE_HOTEL ||
      accommOfferView.offerType === OFFER_TYPE_ALWAYS_ON ||
      accommOfferView.offerType === OFFER_TYPE_LAST_MINUTE ||
      accommOfferView.offerType === OFFER_TYPE_VILLA
    ) {
      const itemGroups = groupBy(accommOfferView.itemViews, (itemView) =>
        getPackageUniqueKey(
          itemView.item.packageId,
          itemView.item.duration,
          (itemView.item as App.Checkout.LEHotelItem).roomRateId,
        ),
      ).values()

      for (const itemGroup of itemGroups) {
        const itemView = itemGroup[0]
        payloads.push({
          type: accommOfferView.offerType,
          code: offerId,
          packageId: itemView.item.packageId,
          startDate,
          endDate,
          price: hasPricesAndValues(itemGroup) ?
            sum(itemGroup, getAccomPrice) :
            undefined,
          roomRateId: (itemView.item as App.Checkout.LEHotelItem).roomRateId,
          roomTypeId: itemView.roomTypeId,
          value: hasPricesAndValues(itemGroup) ?
            sum(itemGroup, (itemView) => itemView.totals.value) :
            undefined,
          occupancies: hasOccupancies(itemGroup) ?
            itemGroup.map((itemView) => ({
              adults: itemView.occupancy.adults,
              childrenAge: itemView.occupancy.childrenAge ?? [],
            })) :
            undefined,
          priceIsInclusive: !itemView.totals.taxesAndFees,
          cartId,
        })
      }
    } else if (accommOfferView.offerType === OFFER_TYPE_BED_BANK) {
      const itemGroups = groupBy(accommOfferView.itemViews, (itemView) =>
        getPackageUniqueKey(
          itemView.item.roomId,
          itemView.item.duration,
          itemView.item.roomRateId,
        ),
      ).values()

      for (const itemGroup of itemGroups) {
        const itemView = itemGroup[0]
        payloads.push({
          type: OFFER_TYPE_BED_BANK,
          code: offerId,
          packageId: itemView.item.roomId,
          startDate,
          endDate,
          occupancies: hasOccupancies(itemGroup) ?
            itemGroup.map((itemView) => ({
              adults: itemView.occupancy.adults,
              childrenAge: itemView.occupancy.childrenAge ?? [],
            })) :
            undefined,
          price: hasPricesAndValues(itemGroup) ?
            sum(itemGroup, getAccomPrice) :
            undefined,
          roomRateId: itemView.item.roomRateId,
          roomTypeId: itemView.item.roomId,
          value: hasPricesAndValues(itemGroup) ?
            sum(itemGroup, (itemView) => itemView.totals.value) :
            undefined,
          priceIsInclusive: !itemView.totals.taxesAndFees,
          cartId,
        })
      }
    } else if (accommOfferView.offerType === OFFER_TYPE_TOUR) {
      for (const itemView of accommOfferView.itemViews) {
        payloads.push({
          type: OFFER_TYPE_TOUR,
          code: offerId,
          startDate,
          endDate,
          price:
            typeof itemView.totals.price === 'number' ?
              itemView.totals.price + (itemView.totals.otherFees?.propertyFees ?? 0) :
              undefined,
          occupancies: itemView.occupancy ?
              [
                {
                  adults: itemView.occupancy.adults,
                  childrenAge: itemView.occupancy.childrenAge ?? [],
                },
              ] :
            undefined,
          subCode: itemView.item.packageId,
          value: itemView.totals.value ?? undefined,
          priceIsInclusive: !itemView.totals.taxesAndFees,
          cartId,
        })
      }
    } else if (accommOfferView.offerType === OFFER_TYPE_TOUR_V2) {
      for (const itemView of accommOfferView.itemViews) {
        payloads.push({
          type: OFFER_TYPE_TOUR_V2,
          code: offerId,
          startDate,
          endDate,
          occupancies: itemView.occupancy ?
              [
                {
                  adults: itemView.occupancy.adults,
                  childrenAge: itemView.occupancy.childrenAge ?? [],
                },
              ] :
            undefined,
          price:
            typeof itemView.totals.price === 'number' ?
              itemView.totals.price + (itemView.totals.otherFees?.propertyFees ?? 0) :
              undefined,
          subCode: itemView.packageId,
          value: itemView.totals.value ?? undefined,
          priceIsInclusive: !itemView.totals.taxesAndFees,
          cartId,
        })
      }
    } else if (accommOfferView.offerType === OFFER_TYPE_CRUISE) {
      for (const itemView of accommOfferView.itemViews) {
        payloads.push({
          type: 'cruise_v2',
          code: offerId,
          occupancy: itemView.occupancy ?
              {
                adults: itemView.occupancy.adults,
                childrenAge: itemView.occupancy.childrenAge ?? [],
              } :
            undefined,
          cabinCategory: itemView.cabinCategory?.id,
          cabinCategoryDetails: itemView.cabinCategory?.imageId ?
              {
                category: itemView.cabinCategory.id,
                imageId: itemView.cabinCategory.imageId,
              } :
            undefined,
          departure:
            itemView.departure?.externalId && itemView.departure.departureDate ?
                {
                  id: itemView.departure.id,
                  externalId: itemView.departure.externalId,
                  departureDate: itemView.departure.departureDate,
                } :
              undefined,
          price: itemView.totals.price,
          value: itemView.totals.value,
          priceIsInclusive: !itemView.totals.taxesAndFees,
          cartId,
        })
      }
    } else if (accommOfferView.offerType === OFFER_TYPE_BUNDLE_AND_SAVE) {
      throw new Error('Bundle and save not yet supported.')
    } else {
      assertUnreachable(accommOfferView.offerType)
    }
  }

  for (const experience of experiences) {
    payloads.push({
      type: 'experience',
      code: experience.id,
      pickupPointId: experience.pickupPoint?.id,
      price: experience.totals.price,
      redemptionLocationId: experience.redemptionLocation?.id,
      startDate: experience.bookingDate,
      startTime: experience.bookingTime,
      tickets: experience.ticketSummaryViews.map((t) => ({
        count: t.count,
        fareType: t.fareType,
        name: t.name,
        ticketId: t.ticketId,
      })),
      value: experience.totals.value ?? undefined,
      languageId: experience.language?.id,
      priceIsInclusive: !experience.totals.taxesAndFees,
      cartId,
    })
  }

  for (const flight of flights) {
    const occupancy = {
      adults: flight.item.occupants.adults,
      childrenAge: flight.item.occupants.childrenAge ?? [],
    }

    const journeys = nonNullable([flight.flights[0], flight.flights[1]]).filter(
      (journey) => journey.journeyFlight.flights.length > 0,
    )

    payloads.push({
      type: 'flight',
      journeys: journeys.map((journey) => {
        const firstFlight = journey.journeyFlight.flights[0]
        const lastFlight = last(journey.journeyFlight.flights)
        return {
          destinationAirportCode: lastFlight.arrivalAirport,
          originAirportCode: firstFlight.departingAirport,
          startDate: firstFlight.departingDate,
          startTime: firstFlight.departingTime,
          endDate: lastFlight.arrivalDate,
          endTime: lastFlight.arrivalTime,
          class: mapToFareClass(firstFlight.fareClass),
          flightNumber: prependCarrierCodeIfNeeded(
            firstFlight.carrier,
            firstFlight.flightNumber,
          ),
        }
      }),
      occupancy,
      price: flight.totals.price ?? undefined,
      adultPrice: flight.totals.price + flight.totals.surcharge,
      value: flight.totals.value,
      priceIsInclusive: !flight.totals.taxesAndFees,
      cartId,
    })
  }

  for (const villaView of villas) {
    const { offerId, checkIn, checkOut } = villaView.item

    payloads.push({
      type: villaView.offer.type,
      code: offerId,
      packageId: villaView.package.id,
      startDate: checkIn,
      endDate: checkOut,
      price: villaView.package.price,
      roomRateId: villaView.package.roomRate?.id,
      roomTypeId: villaView.package.roomType?.id,
      value: villaView.package.value,
      occupancies: [
        {
          adults: villaView.item.occupancy.adults,
          childrenAge: villaView.item.occupancy.childrenAge ?? [],
        },
      ],
      priceIsInclusive: !villaView.package.taxesAndFees,
      cartId,
    })
  }

  // Group hotel+flight bundle if there is one
  const hotelPayloads = payloads.filter(isHotelBookmarkPayload)
  const flightPayloads = payloads.filter(isFlightBookmarkPayload)
  if (hotelPayloads.length === 1 && flightPayloads.length === 1) {
    const hotelPayload = hotelPayloads[0]
    const flightPayload = flightPayloads[0]
    payloads = without(payloads, hotelPayload, flightPayload)
    payloads.push({
      type: 'hotel_flight_bundle',
      hotel: hotelPayload,
      flight: flightPayload,
    })
  }

  return payloads
}

function isHotelBookmarkPayload(
  payload: BookmarkPayload,
): payload is HotelBookmarkPayload {
  return (
    payload.type === OFFER_TYPE_HOTEL ||
    payload.type === OFFER_TYPE_BED_BANK ||
    payload.type === OFFER_TYPE_ALWAYS_ON ||
    payload.type === OFFER_TYPE_LAST_MINUTE
  )
}

function isFlightBookmarkPayload(
  payload: BookmarkPayload,
): payload is FlightBookmarkPayload {
  return payload.type === 'flight'
}
